import { useQuery } from "@tanstack/react-query";
import { Boxes, PackageMinus, ScanLine, Search } from "lucide-react";
import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { Input } from "@/components/ui/input";
import { listBoxes, listCargo, type BoxView, type CargoRow } from "@/api/boxes";
import { groupSscc } from "@/lib/boxes";
import { LoadState } from "@/lib/scan-ui/screen";
import { useDebounced } from "@/lib/use-debounced";
import { cn } from "@/lib/utils";
import { boxesWord, CargoStatusChip, OriginBadge } from "@/pages/boxes/cargo-ui";

/** Как читается статус коробки. */
const STATUS: Record<string, { text: string; cls: string }> = {
  open: { text: "открыта", cls: "bg-sky-100 text-sky-700" },
  empty: { text: "пуста", cls: "bg-slate-200 text-slate-600" },
  cancelled: { text: "отменена", cls: "bg-rose-100 text-rose-700" },
};

/**
 * Действия раздела — то, что кладовщик делает руками.
 *
 * ⚠️ Плитки, а не список коробок. Списка тут был полный перечень всех коробок
 * склада: их будут тысячи, листать их незачем и вредно — коробку находят
 * НАКЛЕЙКОЙ, а не поиском глазами. Экран должен отвечать на вопрос «что я
 * сейчас делаю», а не «какие коробки бывают».
 *
 * Короба и паллеты с упаковки — вкладкой «С упаковки» (их немного и они
 * свежие: последние принятые видны сразу).
 *
 * `merge` (сборка) и `take` (изъятие) ведут на один и тот же скан-экран:
 * механика там одна — поднести сканер, — а плитка называет намерение, чтобы
 * человек, впервые открывший раздел, понимал, что вообще можно делать.
 */
const ACTIONS = [
  {
    to: "/boxes/scan?mode=take",
    icon: PackageMinus,
    title: "Взять из короба",
    hint: "Отсканируйте короб — заводской, початый или с упаковки. Портал спросит, сколько забираете (у короба с упаковки — сканом штук).",
  },
  {
    to: "/boxes/scan",
    icon: Boxes,
    title: "Собрать короб",
    hint: "Сканируйте товар и початые короба — всё уйдёт в новую коробку. Партии и характеристики смешивать можно.",
  },
  {
    to: "/boxes/scan?mode=look",
    icon: ScanLine,
    title: "Что в грузоместе",
    hint: "Коробка склада, короб или паллета с упаковки: состав, история и действия.",
  },
] as const;

function summary(box: BoxView): string {
  if (!box.items.length) return "пусто";
  const total = box.items.reduce((s, i) => s + Number(i.qty_base || 0), 0);
  const first = box.items[0];
  const tail = box.items.length > 1 ? ` и ещё ${box.items.length - 1}` : "";
  return `${first?.nomenclature_name || first?.nomenclature_article || "товар"}${tail} · ${total} шт`;
}

type Tab = "portal" | "packing";

export function BoxesListPage(): React.ReactElement {
  const navigate = useNavigate();
  // Вкладка — в адресе: «назад» с карточки короба упаковки возвращает на неё.
  const [params, setParams] = useSearchParams();
  const tab: Tab = params.get("tab") === "packing" ? "packing" : "portal";
  const setTab = (t: Tab) =>
    setParams(t === "packing" ? { tab: "packing" } : {}, { replace: true });

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div>
        <h1 className="text-xl font-semibold">Грузоместа</h1>
        <p className="mt-1 text-sm text-slate-500">
          Коробки склада и короба/паллеты с упаковки — одной карточкой по скану.
          Початый или сборный короб склада получает свой номер (SSCC); полный
          штатный короб заводить не нужно — у него есть код упаковки.
        </p>
      </div>

      <div className="grid gap-2">
        {ACTIONS.map((a) => (
          <button
            key={a.to}
            onClick={() => navigate(a.to)}
            className="flex items-start gap-3 rounded-lg border bg-white p-4 text-left hover:bg-slate-50"
          >
            <a.icon className="mt-0.5 size-6 shrink-0 text-slate-700" />
            <div className="min-w-0">
              <div className="font-medium">{a.title}</div>
              <div className="mt-0.5 text-sm text-slate-500">{a.hint}</div>
            </div>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 rounded-lg border bg-white p-1 text-sm" role="tablist">
        {(
          [
            ["portal", "Склад"],
            ["packing", "С упаковки"],
          ] as const
        ).map(([key, title]) => (
          <button
            key={key}
            role="tab"
            aria-selected={tab === key}
            onClick={() => setTab(key)}
            className={cn(
              "rounded-md px-3 py-1.5",
              tab === key ? "bg-slate-900 text-white" : "text-slate-600",
            )}
          >
            {title}
          </button>
        ))}
      </div>

      {tab === "portal" ? <PortalSearch /> : <PackingList />}
    </div>
  );
}

/** Коробки склада: только ПОИСК — список всех коробок здесь не нужен (см. `ACTIONS`). */
function PortalSearch(): React.ReactElement {
  const navigate = useNavigate();
  const [q, setQ] = React.useState("");
  // ⚠️ Дебаунс: без него ввод 18-значного номера — восемнадцать запросов,
  // семнадцать из которых выброшены, и каждый идёт `ILIKE '%…%'` мимо
  // уникального индекса плюс подзапросом по составу.
  const query = useDebounced(q, 300);
  const searching = query.trim().length > 0;
  const list = useQuery({
    queryKey: ["boxes", query],
    queryFn: () => listBoxes({ q: query.trim() }),
    // ⚠️ Запрос ТОЛЬКО при поиске: пустая строка означала бы «отдай все
    // коробки склада», то есть ровно тот список, ради которого этот экран и
    // переделан.
    enabled: searching,
  });

  return (
    <>
      <div className="relative">
        <Search className="absolute left-2 top-2.5 size-4 text-slate-400" />
        <Input
          className="pl-8"
          placeholder="Найти по номеру, артикулу или наименованию"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      {searching && (list.isLoading || list.error) && (
        <LoadState
          isLoading={list.isLoading}
          error={list.error}
          onRetry={() => void list.refetch()}
        />
      )}

      {searching && list.data?.length === 0 && (
        <div className="rounded-lg border bg-white p-4 text-center text-sm text-slate-500">
          Ничего не нашлось.
        </div>
      )}

      <div className="space-y-2">
        {list.data?.map((box) => {
          const st = STATUS[box.status] ?? { text: box.status, cls: "bg-slate-100" };
          return (
            <button
              key={box.id}
              onClick={() => navigate(`/boxes/${box.id}`)}
              className="flex w-full items-center gap-3 rounded-lg border bg-white p-3 text-left hover:bg-slate-50"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm">{groupSscc(box.sscc)}</span>
                  <OriginBadge origin="portal" />
                </div>
                <div className="break-words text-xs text-slate-500">{summary(box)}</div>
              </div>
              {box.characteristic_name && (
                <span className="shrink-0 rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                  {box.characteristic_name}
                </span>
              )}
              <span className={`shrink-0 rounded px-2 py-0.5 text-xs ${st.cls}`}>{st.text}</span>
            </button>
          );
        })}
      </div>
    </>
  );
}

function cargoSummary(row: CargoRow): string {
  const name = row.nomenclature_name || row.nomenclature_article;
  const qty = row.left !== row.quantity ? `${row.left} из ${row.quantity} шт` : `${row.quantity} шт`;
  if (row.kind === "pallet") {
    return [boxesWord(row.boxes_count), name, row.boxes_count ? qty : ""]
      .filter(Boolean)
      .join(" · ");
  }
  return [name, row.characteristic_name || "без характеристики", qty].filter(Boolean).join(" · ");
}

/**
 * Короба и паллеты с упаковки: последние принятые сразу, поиск — по SSCC, коду
 * паллеты, коду штуки ЧЗ (можно сканером в поле) или товару.
 */
function PackingList(): React.ReactElement {
  const navigate = useNavigate();
  const [q, setQ] = React.useState("");
  const query = useDebounced(q, 300);
  const list = useQuery({
    queryKey: ["cargo-list", query.trim()],
    queryFn: () => listCargo({ q: query.trim() }),
  });

  return (
    <>
      <div className="relative">
        <Search className="absolute left-2 top-2.5 size-4 text-slate-400" />
        <Input
          className="pl-8"
          placeholder="SSCC, код паллеты, код штуки или товар"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      {(list.isLoading || list.error) && (
        <LoadState
          isLoading={list.isLoading}
          error={list.error}
          onRetry={() => void list.refetch()}
        />
      )}

      {list.data?.length === 0 && (
        <div className="rounded-lg border bg-white p-4 text-center text-sm text-slate-500">
          {query.trim() ? "Ничего не нашлось." : "С упаковки пока ничего не пришло."}
        </div>
      )}

      <div className="space-y-2">
        {list.data?.map((row) => (
          <button
            key={`${row.kind}:${row.code}`}
            onClick={() => navigate(`/boxes/cargo/${row.code}`)}
            className="flex w-full flex-col gap-1 rounded-lg border bg-white p-3 text-left hover:bg-slate-50"
          >
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-slate-500">
                {row.kind === "pallet" ? "Паллета" : "Короб"}
              </span>
              <span className="font-mono text-sm">{groupSscc(row.code)}</span>
              <OriginBadge origin="packing" />
            </div>
            <div className="break-words text-xs text-slate-500">{cargoSummary(row)}</div>
            <div className="flex flex-wrap items-center gap-2">
              <CargoStatusChip status={row.status} text={row.status_text} />
              {row.pallet_code && (
                <span className="text-[11px] text-slate-500">
                  на паллете {groupSscc(row.pallet_code)}
                </span>
              )}
            </div>
          </button>
        ))}
      </div>
    </>
  );
}
